/**
 * Provider and hook to allow an auth token to be stored in global state.
 * To retrieve the objects related to the token from a component use:
 * import { useGlobalContext } from '../path_to/hooks';
 * const { userToken, isLoggedIn, setUserData } = useGlobalContext();
 *
 * Adapted from https://egghead.io/lessons/react-store-a-token-globally-with-react-context-for-easier-use-anywhere-in-a-next-js-app
 */
import React, { createContext, useState } from 'react'
import {
  FCMENU_EMPLOYEE_ID_TOKEN_KEY,
  FCMENU_LOGIN_TOKEN_KEY,
  FCMENU_WAREHOUSE_ID_TOKEN_KEY,
  FCMENU_MOC_DUMMY_USER_TOKEN
} from '../constants/loginConfiguration';
import { BackendServiceClientInterceptor } from '../backend_service/BackendServiceClientInterceptor';
import { BackendServiceClient } from '../backend_service/BackendServiceClient';

/**
 * Fields that are returned from the MyResults backend GetUserDetails API
 * that we want to save in the user's details.
 */
export interface MyResultsUserDetails {
  isAdmin: boolean;
  managerLogin: string;
  emailAddress: string;
  locale: string;
  employeeId?: string;
  login?: string;
  warehouseId?: string;
}

/**
 * Fields about the current user that is logged in.
 * These fields include:
 * 1. Fields extracted from the FCMenu JWT token
 * 2. Fields in MyResultsUserDetails
 */
export interface UserDetails extends MyResultsUserDetails {
  employeeId: string;
  login: string;
  warehouseId: string;
  locale: string;
  isAdmin: boolean;
  managerLogin: string;
}

/**
 * Preferences of the current user for how the page should be displayed.
 */
export interface UserPreferences {
}

/**
 * The target user is the user for which performance data will be looked up.
 * In most situations this will be the user that is currently logged in, but
 * it can be manually specified by admins for debugging/development purposes.
 */
export interface TargetUser {
  employeeId: string;
  login: string;
}

export interface GlobalContextValues {
  userToken: string;
  userDetails: UserDetails;
  userPreferences: UserPreferences;
  targetUser: TargetUser,
  isLoggedIn: boolean;
  setUserData: (_token: string, _userDetails: MyResultsUserDetails|null) => void;
  setUserPreferences: (_preferences: UserPreferences) => void;
  setUserDetails: (_userDetails: UserDetails) => void;
  setTargetUser: (_targetUser: TargetUser) => void;
  isAdminViewEnabled: boolean,
  setIsAdminViewEnabled: (_adminViewStatus: boolean) => void;
  backendServiceClient: BackendServiceClient;
}

const backendServiceClient = new BackendServiceClientInterceptor();

const initialState: GlobalContextValues = {
  userToken: '',
  userDetails: {
    employeeId: '',
    login: '',
    emailAddress: '',
    warehouseId: '',
    locale: '',
    isAdmin: false,
    managerLogin: '',
  },
  userPreferences: {
  },
  targetUser: {
    employeeId: '',
    login: '',
  },
  isLoggedIn: false,
  setUserData: (_token: string, _userDetails: MyResultsUserDetails|null) => {},
  setUserPreferences: (_preferences: UserPreferences) => {},
  setUserDetails: (_userDetails: UserDetails) => {},
  setTargetUser: (_targetUser: TargetUser) => {},
  isAdminViewEnabled: false,
  setIsAdminViewEnabled: (_adminViewStatus: boolean) => {},
  backendServiceClient,
};

export const GlobalContext = createContext<GlobalContextValues>(initialState);

export const GlobalContextProvider = ({children}: any) => {
  const [userToken, setUserToken] = useState('' as string);
  const [userDetails, setUserDetails] = useState({
    employeeId: '',
    login: '',
    emailAddress: '',
    warehouseId: '',
    locale: '',
    isAdmin: false,
    managerLogin: '',
  } as UserDetails);
  const [userPreferences, setUserPreferences] = useState({} as UserPreferences);
  const [targetUser, setTargetUser] = useState({
    employeeId: '',
    login: '',
  } as TargetUser);

  const isLoggedIn = (userToken?.length || 0) > 0;
  const [isAdminViewEnabled, setIsAdminViewEnabled] = useState(userDetails.isAdmin);

  const setUserData = (userToken: string, userDetails: MyResultsUserDetails|null) => {
    setUserToken(userToken);
    let textToDecode = userToken.split('.')[1];
    if (userToken === FCMENU_MOC_DUMMY_USER_TOKEN) {
      setUserDetails({
        employeeId: userDetails?.employeeId!,
        login: userDetails!.login!,
        emailAddress: userDetails?.emailAddress || '',
        warehouseId: userDetails?.warehouseId!,
        locale: userDetails?.locale || Intl.DateTimeFormat().resolvedOptions().timeZone,
        isAdmin: userDetails?.isAdmin || false,
        managerLogin: userDetails?.managerLogin || '',
      });
      setTargetUser({
        employeeId: userDetails?.employeeId!,
        login: userDetails?.login!,
      });
    } else if (textToDecode != undefined) {
      let decodedText = JSON.parse(atob(textToDecode));
      setIsAdminViewEnabled(userDetails?.isAdmin || false);
      setUserDetails({
        employeeId: decodedText[FCMENU_EMPLOYEE_ID_TOKEN_KEY],
        login: decodedText[FCMENU_LOGIN_TOKEN_KEY],
        emailAddress: userDetails?.emailAddress || '',
        warehouseId: decodedText[FCMENU_WAREHOUSE_ID_TOKEN_KEY],
        locale: userDetails?.locale || Intl.DateTimeFormat().resolvedOptions().timeZone,
        isAdmin: userDetails?.isAdmin || false,
        managerLogin: userDetails?.managerLogin || '',
      });
      setTargetUser({
        employeeId: decodedText[FCMENU_EMPLOYEE_ID_TOKEN_KEY],
        login: decodedText[FCMENU_LOGIN_TOKEN_KEY],
      });
    } else {
      setUserDetails({
        employeeId: '',
        login: '',
        emailAddress: '',
        warehouseId: '',
        locale: '',
        isAdmin: false,
        managerLogin: ''
      });
      setTargetUser({
        employeeId: '',
        login: '',
      });
    }
  }
  return (
    <GlobalContext.Provider value={
      {
        userToken, // JWT token of the logged in user
        userDetails, // Details about the logged in user
        userPreferences, // Preferences of the logged in user
        targetUser, // User for which performance data should be retrieved for
        isLoggedIn, // Whether or not a user is currently logged in
        setUserData, // Set the user's JWT token and details from the MRP backend
        setUserPreferences, // Set the prefences of the user
        setTargetUser, // Set the target user
        setUserDetails, //
        isAdminViewEnabled,
        setIsAdminViewEnabled,
        backendServiceClient, // Client for calling the MRP backend
      }
    }>
      {children}
    </GlobalContext.Provider>
  );
};